import React, { useEffect, useState } from 'react';
import {
  Card,
  Col,
  Row,
  Typography,
  Input,
  Button,
  Space,
  DatePicker,
  Divider,
  Select
} from 'antd';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import {
  apiCreateConfig,
  apiCreateReplacement,
  apiGetConfigById,
  apiGetReplacementById,
  apiUpdateConfig,
  apiUpdateReplacement
} from '@services/ManageDevices/DeviceServices';
import { showSuccess } from '@components/notification';
import CustomerSelect from '@components/Select/SelectCustomer';
import DeviceSelect from '@components/Select/SelectDevice';
import dayjs from 'dayjs';
import moment from 'moment';
import { ArrowBack } from '@mui/icons-material';

const { Title } = Typography;
const { Option } = Select;

// Validation schema with necessary fields
const validationSchema = Yup.object({
  customer_id: Yup.string().required('Trường này là bắt buộc nhập!'),
  customer_name: Yup.string().required('Trường này là bắt buộc nhập!'),
  period: Yup.string().required('Trường này là bắt buộc nhập!'),
  end_record_date: Yup.date().required('Trường này là bắt buộc nhập!').nullable()
});

export const Add = ({ disabled }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({});
  const [loadingFetch, setLoadingFetch] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      id ? await apiUpdateConfig(values, id) : await apiCreateConfig(values);
      showSuccess({ message: 'Thành công!' });
      navigate('/configs');
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };
  const generateMonthOptions = () => {
    const months = [];
    const current = moment();
    const startOfYear = current.clone().startOf('year');

    for (let m = 0; m <= current.month() + 1; m++) {
      const monthMoment = startOfYear.clone().add(m, 'months');
      months.push({
        label: `Tháng cước ${monthMoment.format('M')}`,
        value: monthMoment.format('YYYY-MM-DD')
      });
    }
    return months;
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
  };

  const fetch = async () => {
    try {
      setLoadingFetch(true);
      const res = await apiGetConfigById(Number(id));
      if (res) {
        setConfig(res.data.data);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoadingFetch(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetch();
    }
  }, [id]);

  return (
    <Card
      title={id ? 'Cập nhật cấu hình ghi số' : 'Thêm mới cấu hình ghi số'}
      loading={loadingFetch}
      bodyStyle={{ padding: '24px', paddingTop: '0px' }}>
      <Formik
        enableReinitialize
        initialValues={{
          customer_id: config?.customer_id ?? '',
          customer_name: config?.customer_name ?? '',
          start_record_date: config?.start_record_date ? dayjs(config?.start_record_date) : null,
          end_record_date: config?.end_record_date ? dayjs(config?.end_record_date) : null,
          period: config?.period ?? ''
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}>
        {({ setFieldValue, values }) => (
          <Form>
            <Row gutter={[16, 16]}>
              <Col md={20} xs={24}>
                <Row gutter={[16, 16]}>
                  <Col md={12} xs={24}>
                    <Title level={5}>Chọn khách hàng (*)</Title>
                    <Field name="customer_id">
                      {({ field }) => (
                        <CustomerSelect
                          {...field}
                          disabled={disabled}
                          initValue={config?.customer_id}
                          onChange={(value) => {
                            setFieldValue('customer_id', value?.value);
                            setFieldValue('customer_name', value?.label);
                          }}
                        />
                      )}
                    </Field>
                    <ErrorMessage name="customer_id" component="div" style={{ color: '#e11d48' }} />
                  </Col>
                  <Col md={12} xs={24}>
                    <Title level={5}>Chọn kỳ ghi số (*)</Title>
                    <Field name="period">
                      {({ field }) => (
                        <Select
                          disabled={disabled}
                          style={{ width: '100%', height: '36px' }}
                          placeholder="Lựa chọn"
                          value={field.value}
                          onChange={(value) => {
                            setSelectedMonth(value);
                            setFieldValue('period', value);
                          }}>
                          {generateMonthOptions().map((month) => (
                            <Option key={month.value} value={month.value}>
                              {month.label}
                            </Option>
                          ))}
                        </Select>
                      )}
                    </Field>
                    <ErrorMessage name="customer_id" component="div" style={{ color: '#e11d48' }} />
                  </Col>
                </Row>

                <Row gutter={[16, 16]}>
                  <Col md={12} xs={24}>
                    <Title level={5}>Ngày bắt đầu ghi số</Title>
                    <DatePicker
                      disabled={disabled}
                      value={values?.start_record_date}
                      style={{ width: '100%', height: '36px' }}
                      onChange={(date) => setFieldValue('start_record_date', date)}
                      placeholder="Chọn"
                    />
                    <ErrorMessage
                      name="start_record_date"
                      component="div"
                      style={{ color: '#e11d48' }}
                    />
                  </Col>
                  <Col md={12} xs={24}>
                    <Title level={5}>Ngày kết thúc ghi số (*)</Title>
                    <DatePicker
                      disabled={disabled}
                      value={values?.end_record_date}
                      style={{ width: '100%', height: '36px' }}
                      onChange={(date) => setFieldValue('end_record_date', date)}
                      placeholder="Chọn"
                    />
                    <ErrorMessage name="end_record_date" component="div" style={{ color: 'red' }} />
                  </Col>
                </Row>

                <Row gutter={[16, 16]} style={{ marginTop: '32px' }}>
                  <Col>
                    <Space>
                      <Button
                        type="primary"
                        onClick={() => navigate(-1)}
                        icon={<ArrowBack />}
                        style={{
                          color: '#fff',
                          backgroundColor: '#f7b924',
                          borderColor: '#f7b924'
                        }}>
                        Quay lại
                      </Button>
                      {!disabled && (
                        <Button
                          type="primary"
                          htmlType="submit"
                          icon={<SaveOutlined />}
                          loading={loading}>
                          {id ? 'Cập nhật' : 'Thêm mới'}
                        </Button>
                      )}
                    </Space>
                  </Col>
                </Row>
              </Col>
              <Col md={8} xs={24}></Col>
            </Row>
          </Form>
        )}
      </Formik>
    </Card>
  );
};
